import React, { type FC, useMemo } from 'react';
import { Inbox } from 'lucide-react';
import List from '@mui/material/List';

import NavigationLink from 'src/components/layout/landlord/NavigationLink';
import { useInboxes } from 'src/services/api/inboxes/use-inboxes';
import { useInboxFavorites } from 'src/services/api/inboxes/use-inbox-favorites';
import { usePrimaryInbox } from 'src/services/api/inboxes/use-primary-inbox';

import styles from 'src/components/layout/landlord/DesktopNavigationSidebar.module.css';

type Props = {
    onNavigationChange: () => void;
    isSidebarCollapsed: boolean;
}

export const DesktopNavigationInboxesLink: FC<Props> = ({ onNavigationChange, isSidebarCollapsed }) => {
    const { data: inboxes } = useInboxes();
    const { data: viewFavorites } = useInboxFavorites();
    const { primaryInboxId } = usePrimaryInbox();

    const childrenViews = useMemo(() => {
        if (!inboxes || !viewFavorites) {
            return [];
        }
        const favoriteViewIDs = new Set(viewFavorites.map(fav => fav.inbox_id));
        const primaryInbox = primaryInboxId !== 'default' ? inboxes.find(inbox => inbox.id === primaryInboxId) : undefined;

        const teamInbox = (
            <NavigationLink
                key="default-team-inbox"
                text={'Team Inbox'}
                link={'/landlord/inner/inboxes/default'}
                onClick={onNavigationChange}
            />
        );

        const primaryInboxLink = primaryInbox ? (
            <NavigationLink
                key={primaryInbox.id}
                text={primaryInbox.name}
                link={`/landlord/inner/inboxes/${primaryInbox.id}`}
                onClick={onNavigationChange}
            />
        ) : null;

        const favorites = inboxes
            .filter(inbox => favoriteViewIDs.has(inbox.id) && inbox.id !== primaryInbox?.id)
            .map(view => (
                <NavigationLink
                    key={view.id}
                    text={view.name}
                    link={`/landlord/inner/inboxes/${view.id}`}
                    onClick={onNavigationChange}
                />
            ));

        return [primaryInboxLink, teamInbox, ...favorites].filter(Boolean);
    }, [inboxes, viewFavorites, primaryInboxId, isSidebarCollapsed]);

    return (
        <>
            <NavigationLink
                text={isSidebarCollapsed ? '' : 'Inboxes'}
                icon={<Inbox />}
                link="/landlord/inner/inboxes"
                onClick={onNavigationChange}
            />
            {!isSidebarCollapsed && (<List className={styles.inboxLinks}>
                {childrenViews}
            </List>)}
        </>);
};
